import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MetricsPanel from './components/MetricsPanel';
import ComplianceTypeChart from './components/ComplianceTypeChart';
import LocationComplianceChart from './components/LocationComplianceChart';
import ComplianceTrendsChart from './components/ComplianceTrendsChart';
import PriorityAlertsPanel from './components/PriorityAlertsPanel';
import RiskExposureMetrics from './components/RiskExposureMetrics';
import AuditReadinessScore from './components/AuditReadinessScore';

const StatutoryComplianceAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [dateRange, setDateRange] = useState('30days');
  const [selectedRegulationType, setSelectedRegulationType] = useState('all');

  const overallMetrics = {
    totalCompliances: 156,
    completionRate: 87.2,
    completionTrend: 4.3,
    overdueItems: 12,
    overdueTrend: -15.2,
    upcomingDeadlines: 23,
    upcomingTrend: 8.1,
    auditReadiness: 89.5,
    auditTrend: 3.7
  };

  const complianceByType = [
    { name: 'Environmental', total: 45, completed: 42, rate: 93.3, overdue: 2, upcoming: 8, color: '#10b981' },
    { name: 'Safety', total: 52, completed: 46, rate: 88.5, overdue: 4, upcoming: 9, color: '#3b82f6' },
    { name: 'Operational', total: 38, completed: 31, rate: 81.6, overdue: 5, upcoming: 4, color: '#f59e0b' },
    { name: 'Labor', total: 21, completed: 19, rate: 90.5, overdue: 1, upcoming: 2, color: '#8b5cf6' }
  ];

  const locationCompliance = [
    { location: 'Plant 1', total: 67, completed: 61, rate: 91.0, overdue: 3, atRisk: 2 },
    { location: 'Plant 2', total: 54, completed: 45, rate: 83.3, overdue: 6, atRisk: 3 },
    { location: 'Plant 3', total: 35, completed: 32, rate: 91.4, overdue: 3, atRisk: 0 }
  ];

  const complianceTrends = [
    { month: 'Jul', compliant: 89, atRisk: 8, overdue: 3 },
    { month: 'Aug', compliant: 91, atRisk: 6, overdue: 3 },
    { month: 'Sep', compliant: 88, atRisk: 9, overdue: 3 },
    { month: 'Oct', compliant: 92, atRisk: 5, overdue: 3 },
    { month: 'Nov', compliant: 94, atRisk: 4, overdue: 2 },
    { month: 'Dec', compliant: 95, atRisk: 3, overdue: 2 }
  ];

  const priorityAlerts = [
    {
      id: 1,
      type: 'overdue',
      severity: 'high',
      title: 'Environmental Compliance Overdue',
      description: 'Waste disposal permit renewal expired 5 days ago',
      regulation: 'EPA Regulation 40 CFR 262',
      dueDate: '12/01/2025',
      location: 'Plant 2',
      action: 'Immediate Action Required'
    },
    {
      id: 2,
      type: 'upcoming',
      severity: 'medium',
      title: 'Safety Inspection Due',
      description: 'Annual fire safety inspection scheduled within 7 days',
      regulation: 'OSHA 1910.157',
      dueDate: '12/13/2025',
      location: 'Plant 1',
      action: 'Schedule Inspector'
    },
    {
      id: 3,
      type: 'at-risk',
      severity: 'medium',
      title: 'Documentation Gap Identified',
      description: 'Missing safety training records for 5 employees',
      regulation: 'OSHA 1910.120',
      dueDate: '12/20/2025',
      location: 'Plant 3',
      action: 'Update Records'
    }
  ];

  const riskExposure = {
    byRegulationType: [
      { type: 'Environmental', high: 2, medium: 5, low: 38, total: 45 },
      { type: 'Safety', high: 4, medium: 8, low: 40, total: 52 },
      { type: 'Operational', high: 5, medium: 6, low: 27, total: 38 },
      { type: 'Labor', high: 1, medium: 2, low: 18, total: 21 }
    ],
    totalHighRisk: 12,
    totalMediumRisk: 21,
    totalLowRisk: 123
  };

  const auditReadiness = {
    overallScore: 89.5,
    documentationComplete: 94.2,
    trainingUpToDate: 87.8,
    equipmentCertified: 91.3,
    proceduresUpdated: 84.6,
    gapAnalysis: [
      { category: 'Documentation', score: 94.2, gaps: 3 },
      { category: 'Training Records', score: 87.8, gaps: 8 },
      { category: 'Equipment Certs', score: 91.3, gaps: 5 },
      { category: 'Procedures', score: 84.6, gaps: 12 }
    ]
  };

  const locations = [
    { value: 'all', label: 'All Locations' },
    { value: 'plant-1', label: 'Plant 1 - Main Facility' },
    { value: 'plant-2', label: 'Plant 2 - North Wing' },
    { value: 'plant-3', label: 'Plant 3 - South Wing' }
  ];

  const dateRanges = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: 'year', label: 'Last Year' }
  ];

  const regulationTypes = [
    { value: 'all', label: 'All Regulation Types' },
    { value: 'environmental', label: 'Environmental' },
    { value: 'safety', label: 'Safety' },
    { value: 'operational', label: 'Operational' },
    { value: 'labor', label: 'Labor' }
  ];

  const handleExportReport = () => {
    console.log('Exporting statutory compliance report');
  };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Statutory Compliance Analytics Dashboard
            </h1>
            <p className="text-muted-foreground">
              Real-time visibility into regulatory requirements and compliance status across all locations
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportReport}
              className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="Download" size={20} />
              <span className="text-sm font-medium">Export Report</span>
            </button>
            <button
              onClick={() => navigate('/compliance-task-manager')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Icon name="Calendar" size={20} />
              <span className="text-sm font-medium">View Tasks</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="MapPin" size={18} color="var(--color-muted-foreground)" />
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e?.target?.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {locations?.map((location) => (
                <option key={location?.value} value={location?.value}>
                  {location?.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Filter" size={18} color="var(--color-muted-foreground)" />
            <select
              value={selectedRegulationType}
              onChange={(e) => setSelectedRegulationType(e?.target?.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {regulationTypes?.map((type) => (
                <option key={type?.value} value={type?.value}>
                  {type?.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={18} color="var(--color-muted-foreground)" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {dateRanges?.map((range) => (
                <option key={range?.value} value={range?.value}>
                  {range?.label}
                </option>
              ))}
            </select>
          </div>
          <button className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
            <Icon name="RefreshCw" size={16} />
            Refresh Data
          </button>
        </div>
      </div>

      {/* Metrics Panel */}
      <MetricsPanel metrics={overallMetrics} />

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ComplianceTypeChart data={complianceByType} />
        <LocationComplianceChart data={locationCompliance} />
      </div>

      {/* Compliance Trends */}
      <ComplianceTrendsChart data={complianceTrends} />

      {/* Risk and Audit Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <RiskExposureMetrics data={riskExposure} />
        <AuditReadinessScore data={auditReadiness} />
      </div>

      {/* Priority Alerts */}
      <PriorityAlertsPanel alerts={priorityAlerts} />

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <button
          onClick={() => navigate('/compliance-task-manager')}
          className="flex items-center gap-4 p-6 bg-card border border-border rounded-lg hover:border-primary hover:shadow-lg transition-all group"
        >
          <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
            <Icon name="Calendar" size={24} color="var(--color-primary)" />
          </div>
          <div className="flex-1 text-left">
            <h3 className="font-semibold text-foreground mb-1">Task Manager</h3>
            <p className="text-sm text-muted-foreground">Manage upcoming compliance tasks</p>
          </div>
        </button>

        <button
          onClick={() => navigate('/statutory-compliance-documents')}
          className="flex items-center gap-4 p-6 bg-card border border-border rounded-lg hover:border-primary hover:shadow-lg transition-all group"
        >
          <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
            <Icon name="FileText" size={24} color="#8b5cf6" />
          </div>
          <div className="flex-1 text-left">
            <h3 className="font-semibold text-foreground mb-1">Document Repository</h3>
            <p className="text-sm text-muted-foreground">Access compliance documentation</p>
          </div>
        </button>

        <button
          onClick={() => navigate('/statutory-compliance-timeline')}
          className="flex items-center gap-4 p-6 bg-card border border-border rounded-lg hover:border-primary hover:shadow-lg transition-all group"
        >
          <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
            <Icon name="Clock" size={24} color="#10b981" />
          </div>
          <div className="flex-1 text-left">
            <h3 className="font-semibold text-foreground mb-1">Timeline & Validation</h3>
            <p className="text-sm text-muted-foreground">Track compliance timelines</p>
          </div>
        </button>
      </div>
    </div>
  );
};

export default StatutoryComplianceAnalyticsDashboard;